'use client';

import { useState, useCallback, memo } from 'react';
import { LocaleLink } from '@/core/i18n';
import {
  ArrowRight, ChevronDown,
  Brain, Bot, Cpu, Sparkles, TrendingUp, BarChart3, Eye, Mic,
  Star, MessageSquare, UserCheck, Reply, Shield, AlertTriangle,
  Moon, SprayCan, Calendar, Cog, Wrench, Leaf, Scale, Search,
  Heart, Network, Box, Plane,
  Building2, Hotel, Palmtree, Layers,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { AI_FEATURES_CONTENT } from '@/core/i18n/content/ai-features.content';
import type { AIFeaturesContent, AIFeatureCategory } from '@/core/i18n/content/ai-features.types';
import { featureStatusAt, type AIFeatureStatus } from '@/core/i18n/content/ai-features.status';
import { AIFeaturesROI } from './AIFeaturesROI';

/* -------------------------------------------------------------------------- */
/*  Icon maps                                                                  */
/* -------------------------------------------------------------------------- */

const CATEGORY_ICONS: LucideIcon[][] = [
  [TrendingUp, BarChart3, Eye, Cpu, Search],
  [Bot, Mic, MessageSquare, Star, Brain, Sparkles, UserCheck, Reply],
  [Moon, SprayCan, Calendar, Cog],
  [Wrench, Leaf],
  [Scale, AlertTriangle],
  [Heart, Network, Box, Plane],
];

const USE_CASE_ICONS: LucideIcon[] = [Building2, Hotel, Palmtree, Layers];

/* -------------------------------------------------------------------------- */
/*  Feature Card                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Shipping status, shown on every card.
 *
 * Without this the page presented 25 capabilities identically, so a reader had
 * no way to tell the two that work from the nineteen that answer 501. Saying
 * "Available now" on the ones that ship — and "In development" / "Planned" on
 * the rest — is what makes the roadmap a roadmap rather than a claim.
 */
const STATUS_LABEL: Record<AIFeatureStatus, string> = {
  live: 'Available now',
  building: 'In development',
  planned: 'Planned',
};

const STATUS_STYLE: Record<AIFeatureStatus, string> = {
  live: 'bg-hms-green/10 text-hms-green ring-1 ring-hms-green/20',
  building: 'bg-hms-gold/10 text-hms-gold ring-1 ring-hms-gold/20',
  planned: 'bg-hms-surface text-hms-textDim ring-1 ring-hms-border',
};

const FeatureCard = memo(function FeatureCard({ title, description, icon: Icon, id, status }: {
  title: string; description: string; icon: LucideIcon; id: string; status?: AIFeatureStatus;
}) {
  return (
    <div id={id} className={cn(
      'group relative rounded-xl border border-hms-border bg-hms-card p-5',
      'transition-all duration-300',
      'hover:border-hms-gold/20 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20',
    )}>
      <div className={cn(
        'mb-3 flex h-10 w-10 items-center justify-center rounded-lg',
        'bg-hms-gold/10 transition-colors duration-300 group-hover:bg-hms-gold/15',
      )}>
        <Icon className="h-5 w-5 text-hms-gold" />
      </div>
      <h4 className="mb-1.5 text-sm font-bold text-hms-text tracking-tight">{title}</h4>
      <p className="text-[13px] leading-relaxed text-hms-textSub">{description}</p>
      {status && (
        <span
          id={`${id}-status`}
          className={cn(
            'mt-3 inline-flex items-center rounded-full px-2.5 py-0.5',
            'text-[11px] font-medium',
            STATUS_STYLE[status],
          )}
        >
          {STATUS_LABEL[status]}
        </span>
      )}
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/*  Category Accordion                                                         */
/* -------------------------------------------------------------------------- */

function CategorySection({ category, idx, icons }: {
  category: AIFeatureCategory; idx: number; icons: LucideIcon[];
}) {
  const [open, setOpen] = useState(idx < 2);
  const toggle = useCallback(() => setOpen(p => !p), []);

  return (
    <div id={`ai-features-category-${idx}`} className={cn(
      'rounded-xl border transition-colors duration-200',
      open ? 'border-hms-gold/20 bg-hms-card' : 'border-hms-border bg-hms-surface hover:border-hms-gold/10',
    )}>
      <button
        id={`ai-features-category-toggle-${idx}`}
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between px-5 py-4 sm:px-6 sm:py-5 text-left"
        aria-expanded={open}
      >
        <div>
          <h3 className={cn(
            'text-lg font-bold tracking-tight sm:text-xl',
            open
              ? 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent'
              : 'text-hms-text',
          )}>
            {category.title}
          </h3>
          <p className="mt-0.5 text-[13px] text-hms-textSub">{category.description}</p>
        </div>
        <ChevronDown className={cn(
          'ml-4 h-5 w-5 shrink-0 text-hms-textDim transition-transform duration-200',
          open && 'rotate-180 text-hms-gold',
        )} />
      </button>
      {open && (
        <div className="px-5 pb-5 sm:px-6 sm:pb-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {category.features.map((feat, fi) => (
              <FeatureCard
                key={fi}
                id={`ai-features-card-${idx}-${fi}`}
                title={feat.title}
                description={feat.description}
                icon={icons[fi] ?? Brain}
                // Position-keyed, not title-keyed: titles are translated in 9
                // languages, positions are not. ai-features.status.test.ts
                // asserts every locale keeps the same shape.
                status={featureStatusAt(idx, fi)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Use Case Card                                                              */
/* -------------------------------------------------------------------------- */

const UseCaseCard = memo(function UseCaseCard({ title, description, stat, statLabel, icon: Icon, id }: {
  title: string; description: string; stat: string; statLabel: string; icon: LucideIcon; id: string;
}) {
  return (
    <div id={id} className={cn(
      'rounded-xl border border-hms-border bg-hms-card p-6 transition-all duration-300',
      'hover:border-hms-gold/20 hover:shadow-lg hover:shadow-black/20',
    )}>
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hms-gold/10">
          <Icon className="h-5 w-5 text-hms-gold" />
        </div>
        <h4 className="text-base font-bold text-hms-text">{title}</h4>
      </div>
      <p className="mb-4 text-[13px] leading-relaxed text-hms-textSub">{description}</p>
      <div className="flex items-baseline gap-2 border-t border-hms-border pt-3">
        <span className="text-2xl font-extrabold text-hms-gold tracking-tight">{stat}</span>
        <span className="text-xs font-medium text-hms-textSub">{statLabel}</span>
      </div>
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/*  FAQ Accordion                                                              */
/* -------------------------------------------------------------------------- */

function FAQItem({ question, answer, idx }: { question: string; answer: string; idx: number }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen(p => !p), []);

  return (
    <div id={`ai-features-faq-${idx}`} className="border-b border-hms-border last:border-b-0">
      <button
        id={`ai-features-faq-toggle-${idx}`}
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between py-4 text-left"
        aria-expanded={open}
      >
        <span className={cn('text-sm font-semibold pr-4', open ? 'text-hms-gold' : 'text-hms-text')}>{question}</span>
        <ChevronDown className={cn(
          'h-4 w-4 shrink-0 text-hms-textDim transition-transform duration-200',
          open && 'rotate-180 text-hms-gold',
        )} />
      </button>
      {open && (
        <p className="pb-4 text-[13px] leading-relaxed text-hms-textSub">{answer}</p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  AIFeaturesPage — Main Export                                               */
/* -------------------------------------------------------------------------- */

export function AIFeaturesPage() {
  const c = usePageContent(AI_FEATURES_CONTENT);

  return (
    <main id="ai-features-page">
      {/* Hero */}
      <section id="ai-features-hero" className="relative w-full overflow-hidden bg-hms-bg">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }} />
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 50% 40% at 80% 60%, rgba(13,158,153,0.04) 0%, transparent 60%)' }} />
        <div className="relative mx-auto max-w-4xl px-4 pb-14 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-36">
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-hms-gold/10">
            <Brain className="h-7 w-7 text-hms-gold" />
          </div>
          <h1 id="ai-features-hero-title" className={cn(
            'font-poppins text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}>
            {c.hero.title}
          </h1>
          <p id="ai-features-hero-subtitle" className="mx-auto mt-5 max-w-2xl text-base text-hms-textSub sm:text-lg">
            {c.hero.subtitle}
          </p>
          <div id="ai-features-hero-actions" className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <LocaleLink
              id="ai-features-hero-trial-btn"
              href="/register-hotel"
              className={cn(
                'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
                'text-sm font-semibold tracking-wide text-hms-bg',
                'shadow-lg shadow-hms-gold/20 transition-all duration-200',
                'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
                'w-full sm:w-auto justify-center',
              )}
            >
              {c.hero.trialBtn}
              <ArrowRight className="h-4 w-4" />
            </LocaleLink>
            <LocaleLink
              id="ai-features-hero-demo-btn"
              href="/contact"
              className={cn(
                'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
                'text-sm font-semibold tracking-wide text-hms-text',
                'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
                'w-full sm:w-auto',
              )}
            >
              {c.hero.demoBtn}
            </LocaleLink>
          </div>

          {/* Stats */}
          <div id="ai-features-stats" className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4 max-w-2xl mx-auto">
            {c.stats.map((stat, idx) => (
              <div key={idx} id={`ai-features-stat-${idx}`} className="flex flex-col items-center rounded-xl border border-hms-border bg-hms-card px-4 py-4">
                <span className="text-xl font-extrabold text-hms-gold tracking-tight">{stat.value}</span>
                <span className="mt-0.5 text-[11px] font-medium text-hms-textSub">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Categories */}
      <section id="ai-features-categories" className="bg-hms-bg py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 space-y-4">
          {c.categories.map((cat, idx) => (
            <CategorySection key={idx} category={cat} idx={idx} icons={CATEGORY_ICONS[idx] ?? []} />
          ))}
        </div>
      </section>

      {/* ROI Calculator */}
      <AIFeaturesROI content={c.roi} />

      {/* Use Cases */}
      <section id="ai-features-use-cases" className="bg-hms-bg py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 id="ai-features-use-cases-title" className={cn(
              'text-2xl font-extrabold tracking-tight sm:text-3xl',
              'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
            )}>
              {c.useCases.title}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-hms-textSub sm:text-base">{c.useCases.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.useCases.items.map((uc, idx) => (
              <UseCaseCard
                key={idx}
                id={`ai-features-use-case-${idx}`}
                title={uc.title}
                description={uc.description}
                stat={uc.stat}
                statLabel={uc.statLabel}
                icon={USE_CASE_ICONS[idx] ?? Building2}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="ai-features-faq" className="bg-hms-surface py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 id="ai-features-faq-title" className={cn(
            'mb-8 text-center text-2xl font-extrabold tracking-tight sm:text-3xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}>
            {c.faq.title}
          </h2>
          <div className="rounded-xl border border-hms-border bg-hms-card px-5 sm:px-6">
            {c.faq.items.map((item, idx) => (
              <FAQItem key={idx} question={item.question} answer={item.answer} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="ai-features-cta" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-24">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <Sparkles className="mx-auto h-8 w-8 text-hms-gold mb-4" />
          <h2 id="ai-features-cta-title" className={cn(
            'text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}>
            {c.cta.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-hms-textSub sm:text-base">{c.cta.subtitle}</p>
          <div id="ai-features-cta-actions" className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <LocaleLink
              id="ai-features-cta-trial-btn"
              href="/register-hotel"
              className={cn(
                'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
                'text-sm font-semibold tracking-wide text-hms-bg',
                'shadow-lg shadow-hms-gold/20 transition-all duration-200',
                'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
                'w-full sm:w-auto justify-center',
              )}
            >
              {c.cta.trialBtn}
              <ArrowRight className="h-4 w-4" />
            </LocaleLink>
            <LocaleLink
              id="ai-features-cta-demo-btn"
              href="/contact"
              className={cn(
                'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
                'text-sm font-semibold tracking-wide text-hms-text',
                'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
                'w-full sm:w-auto',
              )}
            >
              {c.cta.demoBtn}
            </LocaleLink>
          </div>
        </div>
      </section>
    </main>
  );
}
